import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon, List, StatusTip } from "tea-component";
import classnames from 'classnames';
import './index.scss';
import SvgIcon from '@home/components/SvgIcon';
import { useIntl, history, useSelector } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';
import { FRAME_TYPE_MAP } from '@/pages/benchmark/_config/constant';
import { getQuestionList } from '@/pages/benchmark/services/benchmark';
import { buriedBtnPoint } from '@home/utils/buriedPoint';
import { IQuestionList } from './type';
import { formatObjUrl, getLogTypeId } from '@/pages/benchmark/utils/utils';
import TextBubbles from '@home/components/TextBubbles';
import ACard from '@/pages/dashboard/components/ACard';

const BenchmarkQuestionList = React.memo((props: IQuestionList) => {
  const { locale, formatMessage } = useIntl(); // 中英切换方法
  const isEN = locale === LANGUAGE_EN;
  const { title = formatMessage({ id: 'dbb_explore_recommended_questions' }), className, style = {}, onClickItem, onRefreshClick, frameType} = props;
  const [list, setList] = useState([]);
  const [range, setRange] = useState([0, 5]);
  const [currentPage, setCurrentPage] = useState(0);
  const current = useMemo(()=> list.slice(range[0], range[1]),[range, list]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const { allMetricConfig } = useSelector((state: any) => ({
    allMetricConfig: state?.benchmark?.metricConfig
  }));

  const onClickRefresh = () => {
    setRange(([first, second]) => {
      if(first + 5 >= list?.length) {
        return [0, 5];
      }
      return [first + 5, second + 5];
    });
    setCurrentPage(prev=> prev+ 1);
    buriedBtnPoint({
      buttonId: `800101003`,
      extInfo: JSON.stringify({
        clicked: currentPage + 1
      }),
      pageId: '800101',
      uidType: '', // 基准打点不包含uidType
      typeId: getLogTypeId(frameType),
    });

    onRefreshClick?.();
  };

  const fetchQuestion = useCallback(async () => {
    setList([]);
    setIsLoading(true);
    setIsError(false);
    const payload = { 
      frame_type: FRAME_TYPE_MAP[frameType || 'pcconsole'],
      page_size: 100
    };
    try {
      const res = await getQuestionList(payload);
      const { data } = res;
      setList(data?.list || []);
      setCurrentPage(0);
      setRange([0, 5]);
    } catch (e) {
      console.log('err', e);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  },[frameType]);

  useEffect(() => {
    fetchQuestion();
  },[fetchQuestion]);

  return (
    <>
      <div className={classnames('dbb-question', className)} style={style}>
        <div className="dbb-question-row">
          <span className={'dbb-question-title'} >{title}</span>
          {
            list?.length > 5 && (
              <SvgIcon type={'shuaxin'} className={'dbb-question-refresh-icon'} onClick={()=> {
                onClickRefresh();
              }}/>
            )
          }
        </div>
        {
          isLoading && (
            <div className='dbb-question-loading'>
              <StatusTip
                status='loading'
                loadingText={formatMessage({ id: 'common_loading'})}
              />
            </div>
          )
        }
        {
          isError && (
            <div className='dbb-question-loading dbb-question-error'>
              <Icon type='error' className='error-icon'/>
              {formatMessage({ id: 'common_loading_failed'})}
            </div>
          )
        }
        {
          !isLoading && !isError && (
            <List className={classnames('dbb-question-list')}>
              {
                current?.map((val: { question_cn: string; question_en: string; filter: { region: string[]; country: string[]; platform: string[]; genre: string; }; metric: string;}, index: number)=> {
                  const name = isEN ? val?.question_en : val?.question_cn;
                  const metricConfig = allMetricConfig?.[val?.metric] || {};
                  const linkPage = metricConfig?.value_type?.trim() === 'otdr' ? `metricOtdr` : `metricDetail`;
                  return (
                    <ACard key={val?.metric + index}  url={`/benchmark/${frameType}/explore/${linkPage}/${val?.metric}?${formatObjUrl(val?.filter)}`} isStopPropagation={false}>
                      <List.Item onClick={()=> {
                        history.push(`/benchmark/${frameType}/explore/${linkPage}/${val?.metric}?${formatObjUrl(val?.filter)}`);
                        onClickItem?.(val);
                      }}>
                        <TextBubbles text={`${index + range[0] + 1}. ${name}`} rowNum={2} className="question"/>
                        {
                          index + range[0] < 3 && (
                            <SvgIcon type="remen" className="flame-icon" />
                          )
                        }
                      </List.Item>
                    </ACard>
                  );
                })
              }
            </List>
          )
        }
      </div>
    </>
  );
});

export default BenchmarkQuestionList;